// frontend/src/services/engineerLeave.service.ts
// Engineer Leave Application Service - Direct to HR Approval (Skip Line Manager)
// WITH PROPER BALANCE TRACKING, DUPLICATE DATE CHECK, CANCELLATION SUPPORT
// PHOENICIAN TECHNICAL SERVICES LLC

import { 
    collection, 
    doc, 
    getDoc, 
    getDocs, 
    setDoc, 
    query, 
    where, 
    Timestamp,
    addDoc,
    serverTimestamp,
    orderBy,
    limit,
    updateDoc,
    increment,
    writeBatch
  } from 'firebase/firestore';
  import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
  import { db, storage } from './firebase';
  
  // Leave Types (matching Flutter app)
  export type LeaveType = 'annual' | 'sick' | 'local' | 'emergency';
  
  export interface LeaveTypeInfo {
    id: LeaveType;
    displayName: string;
    totalDays: number;
    color: string;
    icon: string;
    description: string;
  }
  
  export const LEAVE_TYPES: LeaveTypeInfo[] = [
    { 
      id: 'annual', 
      displayName: 'Annual Leave', 
      totalDays: 30, 
      color: '#2196F3',
      icon: '🏖️',
      description: '30 days per year'
    },
    { 
      id: 'sick', 
      displayName: 'Sick Leave', 
      totalDays: 15, 
      color: '#F44336',
      icon: '🏥',
      description: '15 days per year'
    },
    { 
      id: 'local', 
      displayName: 'Local Leave', 
      totalDays: 10, 
      color: '#4CAF50',
      icon: '📍',
      description: '10 days per year'
    },
    { 
      id: 'emergency', 
      displayName: 'Emergency Leave', 
      totalDays: 15, 
      color: '#FF5722',
      icon: '🚨',
      description: '15 days per year (double deduction from Emergency + Annual)'
    }
  ];
  
  export interface EmployeeSearchResult {
    employeeId: string;
    employeePin: string;
    employeeName: string;
    designation?: string;
    department?: string;
    projectCode?: string;
    lineManagerId?: string;
    lineManagerName?: string;
    photoUrl?: string;
  }
  
  export interface LeaveBalanceDetail {
    total: number;
    used: number;
    pending: number;
    remaining: number;
  }
  
  export interface LeaveBalance {
    annual: LeaveBalanceDetail;
    sick: LeaveBalanceDetail;
    local: LeaveBalanceDetail;
    emergency: LeaveBalanceDetail;
  }
  
  export interface LeaveApplicationData {
    employeeId: string;
    employeeName: string;
    employeePin: string;
    leaveType: LeaveType;
    startDate: Date;
    endDate: Date;
    totalDays: number;
    reason: string;
    isAlreadyTaken: boolean;
    certificateFile?: File;
    engineerRemarks?: string;
    appliedByEngineerId: string;
    appliedByEngineerName: string;
  }
  
  export interface LeaveApplicationResult {
    success: boolean;
    applicationId?: string;
    error?: string;
  }
  
  // Leave application record for reports
  export interface LeaveApplicationRecord {
    id: string;
    applicationId: string;
    employeeId: string;
    employeeName: string;
    employeePin: string;
    department?: string;
    designation?: string;
    leaveType: LeaveType;
    startDate: Date;
    endDate: Date;
    totalDays: number;
    reason: string;
    isAlreadyTaken: boolean;
    certificateUrl?: string | null;
    certificateFileName?: string | null;
    
    // Engineer info
    appliedVia: 'engineer_portal' | 'employee_app' | 'web';
    appliedByEngineerId?: string;
    appliedByEngineerName?: string;
    engineerRemarks?: string;
    
    // HR Approval
    hrApprovalStatus: 'pending' | 'approved' | 'rejected' | 'cancelled';
    hrApprovedAt?: Date | null;
    hrManagerId?: string | null;
    hrManagerName?: string | null;
    hrRemarks?: string | null;
    
    // Cancellation info
    cancelledAt?: Date | null;
    cancelledBy?: string | null;
    cancelledByName?: string | null;
    cancellationReason?: string | null;
    
    // Dates
    applicationDate: Date;
    createdAt: Date;
    updatedAt?: Date;
    isActive: boolean;
  }
  
  // Date overlap check result
  interface DateOverlapResult {
    hasOverlap: boolean;
    conflictingLeaves: LeaveApplicationRecord[];
    message?: string;
  }
  
  class EngineerLeaveService {
    
    // ============================================================================
    // EMPLOYEE SEARCH
    // ============================================================================
    
    /**
     * Search employee by PIN/Number in MasterSheet
     */
    async searchEmployeeByPin(pin: string): Promise<EmployeeSearchResult | null> {
      try {
        console.log('🔍 Searching employee by PIN:', pin);
        
        // Clean the PIN - remove EMP prefix if present
        const cleanPin = pin.toUpperCase().replace('EMP', '').trim();
        
        // Try multiple PIN formats
        const pinVariants = [
          cleanPin,
          `EMP${cleanPin}`,
          pin.trim()
        ];
        
        for (const pinVariant of pinVariants) {
          // Search in MasterSheet/Employee-Data/employees
          const employeeDoc = await getDoc(
            doc(db, 'MasterSheet', 'Employee-Data', 'employees', pinVariant)
          );
          
          if (employeeDoc.exists()) {
            const data = employeeDoc.data();
            console.log('✅ Employee found:', data.employeeName || data.name);
            
            return {
              employeeId: employeeDoc.id,
              employeePin: data.employeeNumber || pinVariant,
              employeeName: data.employeeName || data.name || 'Unknown',
              designation: data.designation || 'Worker',
              department: data.department || 'General',
              projectCode: data.projectCode || data.currentProject,
              lineManagerId: data.lineManagerId,
              lineManagerName: data.lineManagerName,
              photoUrl: data.photoUrl || data.profilePhoto
            };
          }
        }
        
        // Also try query-based search
        const employeesRef = collection(db, 'MasterSheet', 'Employee-Data', 'employees');
        const q = query(employeesRef, where('employeeNumber', '==', cleanPin));
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
          const docSnap = querySnapshot.docs[0];
          const data = docSnap.data();
          
          return {
            employeeId: docSnap.id,
            employeePin: data.employeeNumber || cleanPin,
            employeeName: data.employeeName || data.name || 'Unknown',
            designation: data.designation || 'Worker',
            department: data.department || 'General',
            projectCode: data.projectCode || data.currentProject,
            lineManagerId: data.lineManagerId,
            lineManagerName: data.lineManagerName,
            photoUrl: data.photoUrl || data.profilePhoto
          };
        }
        
        console.log('❌ Employee not found for PIN:', pin);
        return null;
        
      } catch (error) {
        console.error('❌ Error searching employee:', error);
        throw new Error('Failed to search employee. Please try again.');
      }
    }
    
    // ============================================================================
    // 🆕 NEW: CHECK FOR OVERLAPPING LEAVE DATES
    // ============================================================================
    
    /**
     * Check if employee has overlapping leave applications for the given date range
     */
    async checkDateOverlap(
      employeeId: string,
      startDate: Date,
      endDate: Date,
      excludeApplicationId?: string
    ): Promise<DateOverlapResult> {
      try {
        console.log('🔍 Checking date overlap for employee:', employeeId);
        console.log(`📅 Requested dates: ${startDate.toISOString()} - ${endDate.toISOString()}`);
        
        const conflictingLeaves: LeaveApplicationRecord[] = [];
        
        // Get all active/pending/approved leaves for this employee
        const collections = ['hr_leave_approvals', 'leave_applications'];
        
        for (const collectionName of collections) {
          try {
            const leaveRef = collection(db, collectionName);
            const q = query(
              leaveRef,
              where('employeeId', '==', employeeId),
              where('isActive', '==', true)
            );
            
            const snapshot = await getDocs(q);
            
            snapshot.docs.forEach(docSnap => {
              const data = docSnap.data();
              
              // Skip cancelled or rejected leaves
              const status = data.hrApprovalStatus || data.status;
              if (status === 'cancelled' || status === 'rejected') {
                return;
              }
              
              // Skip the application being edited (if provided)
              if (excludeApplicationId && (docSnap.id === excludeApplicationId || data.applicationId === excludeApplicationId)) {
                return;
              }
              
              // Parse dates
              const leaveStart = this.parseTimestamp(data.startDate);
              const leaveEnd = this.parseTimestamp(data.endDate);
              
              // Check for date overlap
              // Overlap occurs if: startDate <= existingEnd AND endDate >= existingStart
              const hasOverlap = startDate <= leaveEnd && endDate >= leaveStart;
              
              if (hasOverlap) {
                // Avoid duplicates
                if (!conflictingLeaves.some(l => l.applicationId === (data.applicationId || docSnap.id))) {
                  conflictingLeaves.push(this.parseLeaveApplicationRecord(docSnap.id, data));
                }
              }
            });
          } catch (e) {
            console.log(`Could not query ${collectionName}:`, e);
          }
        }
        
        if (conflictingLeaves.length > 0) {
          const conflictDetails = conflictingLeaves.map(l => 
            `${l.leaveType} (${this.formatDateShort(l.startDate)} - ${this.formatDateShort(l.endDate)})`
          ).join(', ');
          
          console.log('⚠️ Found overlapping leaves:', conflictDetails);
          
          return {
            hasOverlap: true,
            conflictingLeaves,
            message: `Employee already has leave application(s) for overlapping dates: ${conflictDetails}`
          };
        }
        
        console.log('✅ No date overlap found');
        return {
          hasOverlap: false,
          conflictingLeaves: []
        };
        
      } catch (error) {
        console.error('❌ Error checking date overlap:', error);
        // Return no overlap on error to allow submission (will be caught by HR)
        return {
          hasOverlap: false,
          conflictingLeaves: []
        };
      }
    }
    
    private formatDateShort(date: Date): string {
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
    }
    
    // ============================================================================
    // GET EMPLOYEE LEAVE APPLICATIONS (FOR SEARCH RESULTS)
    // ============================================================================
    
    /**
     * Get recent leave applications for an employee
     */
    async getEmployeeLeaveApplications(employeeId: string, maxResults: number = 10): Promise<LeaveApplicationRecord[]> {
      try {
        console.log('🔍 Fetching leave applications for employee:', employeeId);
        
        const applications: LeaveApplicationRecord[] = [];
        const seenIds = new Set<string>();
        
        // Try hr_leave_approvals first (engineer portal applications)
        const hrApprovalRef = collection(db, 'hr_leave_approvals');
        const q1 = query(
          hrApprovalRef, 
          where('employeeId', '==', employeeId),
          orderBy('applicationDate', 'desc'),
          limit(maxResults)
        );
        
        try {
          const snapshot1 = await getDocs(q1);
          snapshot1.docs.forEach(docSnap => {
            const appId = docSnap.data().applicationId || docSnap.id;
            if (!seenIds.has(appId)) {
              seenIds.add(appId);
              applications.push(this.parseLeaveApplicationRecord(docSnap.id, docSnap.data()));
            }
          });
        } catch (e) {
          console.log('Index may be building for hr_leave_approvals, trying simple query');
          // Try without orderBy
          const q1Simple = query(
            hrApprovalRef, 
            where('employeeId', '==', employeeId),
            limit(maxResults)
          );
          const snapshot1 = await getDocs(q1Simple);
          snapshot1.docs.forEach(docSnap => {
            const appId = docSnap.data().applicationId || docSnap.id;
            if (!seenIds.has(appId)) {
              seenIds.add(appId);
              applications.push(this.parseLeaveApplicationRecord(docSnap.id, docSnap.data()));
            }
          });
        }
        
        // Also check leave_applications collection
        if (applications.length < maxResults) {
          const leaveAppRef = collection(db, 'leave_applications');
          const q2 = query(
            leaveAppRef,
            where('employeeId', '==', employeeId),
            limit(maxResults)
          );
          
          try {
            const snapshot2 = await getDocs(q2);
            snapshot2.docs.forEach(docSnap => {
              const appId = docSnap.data().applicationId || docSnap.id;
              if (!seenIds.has(appId) && !seenIds.has(docSnap.id)) {
                seenIds.add(appId);
                applications.push(this.parseLeaveApplicationRecord(docSnap.id, docSnap.data()));
              }
            });
          } catch (e) {
            console.log('Error querying leave_applications:', e);
          }
        }
        
        // Sort by date descending
        applications.sort((a, b) => b.applicationDate.getTime() - a.applicationDate.getTime());
        
        console.log(`✅ Found ${applications.length} leave applications`);
        return applications.slice(0, maxResults);
        
      } catch (error) {
        console.error('❌ Error fetching employee leave applications:', error);
        return [];
      }
    }
    
    /**
     * Parse leave application record from Firestore document
     */
    private parseLeaveApplicationRecord(id: string, data: any): LeaveApplicationRecord {
      return {
        id,
        applicationId: data.applicationId || id,
        employeeId: data.employeeId || '',
        employeeName: data.employeeName || '',
        employeePin: data.employeePin || '',
        department: data.department,
        designation: data.designation,
        leaveType: data.leaveType || 'annual',
        startDate: this.parseTimestamp(data.startDate),
        endDate: this.parseTimestamp(data.endDate),
        totalDays: data.totalDays || 0,
        reason: data.reason || '',
        isAlreadyTaken: data.isAlreadyTaken || false,
        certificateUrl: data.certificateUrl || null,
        certificateFileName: data.certificateFileName || null,
        
        appliedVia: data.appliedVia || 'employee_app',
        appliedByEngineerId: data.appliedByEngineerId,
        appliedByEngineerName: data.appliedByEngineerName,
        engineerRemarks: data.engineerRemarks,
        
        hrApprovalStatus: data.hrApprovalStatus || data.status || 'pending',
        hrApprovedAt: data.hrApprovedAt ? this.parseTimestamp(data.hrApprovedAt) : null,
        hrManagerId: data.hrManagerId || null,
        hrManagerName: data.hrManagerName || null,
        hrRemarks: data.hrRemarks || null,
        
        // Cancellation info
        cancelledAt: data.cancelledAt ? this.parseTimestamp(data.cancelledAt) : null,
        cancelledBy: data.cancelledBy || null,
        cancelledByName: data.cancelledByName || null,
        cancellationReason: data.cancellationReason || null,
        
        applicationDate: this.parseTimestamp(data.applicationDate || data.createdAt),
        createdAt: this.parseTimestamp(data.createdAt || data.applicationDate),
        updatedAt: data.updatedAt ? this.parseTimestamp(data.updatedAt) : undefined,
        isActive: data.isActive !== false
      };
    }
    
    private parseTimestamp(value: any): Date {
      if (!value) return new Date();
      if (value.toDate) return value.toDate();
      if (value instanceof Date) return value;
      if (typeof value === 'string') return new Date(value);
      if (typeof value === 'number') return new Date(value);
      return new Date();
    }
    
    // ============================================================================
    // GET ALL LEAVES APPLIED BY ENGINEER (FOR REPORTS)
    // ============================================================================
    
    /**
     * Get all leave applications submitted by an engineer
     */
    async getEngineerAppliedLeaves(engineerId: string): Promise<LeaveApplicationRecord[]> {
      try {
        console.log('🔍 Fetching leaves applied by engineer:', engineerId);
        
        const applications: LeaveApplicationRecord[] = [];
        const seenIds = new Set<string>();
        
        // Query hr_leave_approvals
        const hrApprovalRef = collection(db, 'hr_leave_approvals');
        
        try {
          const q1 = query(
            hrApprovalRef,
            where('appliedByEngineerId', '==', engineerId),
            orderBy('applicationDate', 'desc')
          );
          
          const snapshot1 = await getDocs(q1);
          snapshot1.docs.forEach(docSnap => {
            const appId = docSnap.data().applicationId || docSnap.id;
            if (!seenIds.has(appId)) {
              seenIds.add(appId);
              applications.push(this.parseLeaveApplicationRecord(docSnap.id, docSnap.data()));
            }
          });
        } catch (e) {
          console.log('Index may be building, trying alternative query');
          
          // Fallback: get all and filter
          const allSnapshot = await getDocs(hrApprovalRef);
          allSnapshot.docs.forEach(docSnap => {
            const data = docSnap.data();
            if (data.appliedByEngineerId === engineerId || data.appliedVia === 'engineer_portal') {
              const appId = data.applicationId || docSnap.id;
              if (!seenIds.has(appId)) {
                seenIds.add(appId);
                applications.push(this.parseLeaveApplicationRecord(docSnap.id, data));
              }
            }
          });
        }
        
        // Also check leave_applications for engineer applied leaves
        const leaveAppRef = collection(db, 'leave_applications');
        try {
          const snapshot2 = await getDocs(leaveAppRef);
          snapshot2.docs.forEach(docSnap => {
            const data = docSnap.data();
            if (data.appliedByEngineerId === engineerId || data.appliedVia === 'engineer_portal') {
              const appId = data.applicationId || docSnap.id;
              if (!seenIds.has(appId) && !seenIds.has(docSnap.id)) {
                seenIds.add(appId);
                applications.push(this.parseLeaveApplicationRecord(docSnap.id, data));
              }
            }
          });
        } catch (e) {
          console.log('Error querying leave_applications');
        }
        
        // Sort by date descending
        applications.sort((a, b) => b.applicationDate.getTime() - a.applicationDate.getTime());
        
        console.log(`✅ Found ${applications.length} leaves applied by engineer`);
        return applications;
        
      } catch (error) {
        console.error('❌ Error fetching engineer applied leaves:', error);
        return [];
      }
    }
    
    /**
     * Get leave report statistics for engineer
     */
    async getEngineerLeaveStats(engineerId: string): Promise<{
      total: number;
      pending: number;
      approved: number;
      rejected: number;
      cancelled: number;
      byType: Record<LeaveType, number>;
      totalDays: number;
      thisMonth: number;
    }> {
      try {
        const leaves = await this.getEngineerAppliedLeaves(engineerId);
        
        const stats = {
          total: leaves.length,
          pending: 0,
          approved: 0,
          rejected: 0,
          cancelled: 0,
          byType: { annual: 0, sick: 0, local: 0, emergency: 0 } as Record<LeaveType, number>,
          totalDays: 0,
          thisMonth: 0
        };
        
        const now = new Date();
        const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        
        leaves.forEach(leave => {
          // Status counts
          if (leave.hrApprovalStatus === 'pending') stats.pending++;
          else if (leave.hrApprovalStatus === 'approved') stats.approved++;
          else if (leave.hrApprovalStatus === 'rejected') stats.rejected++;
          else if (leave.hrApprovalStatus === 'cancelled') stats.cancelled++;
          
          // By type
          if (leave.leaveType && stats.byType[leave.leaveType] !== undefined) {
            stats.byType[leave.leaveType]++;
          }
          
          // Total days (only approved)
          if (leave.hrApprovalStatus === 'approved') {
            stats.totalDays += leave.totalDays || 0;
          }
          
          // This month
          if (leave.applicationDate >= thisMonthStart) {
            stats.thisMonth++;
          }
        });
        
        return stats;
        
      } catch (error) {
        console.error('❌ Error getting engineer leave stats:', error);
        return {
          total: 0,
          pending: 0,
          approved: 0,
          rejected: 0,
          cancelled: 0,
          byType: { annual: 0, sick: 0, local: 0, emergency: 0 },
          totalDays: 0,
          thisMonth: 0
        };
      }
    }
    
    // ============================================================================
    // LEAVE BALANCE - ENHANCED WITH PROPER TRACKING
    // ============================================================================
    
    /**
     * Get leave balance for an employee with proper calculation
     */
    async getLeaveBalance(employeeId: string): Promise<LeaveBalance> {
      try {
        const currentYear = new Date().getFullYear();
        const balanceDocRef = doc(db, 'leave_balances', `${employeeId}_${currentYear}`);
        const balanceDoc = await getDoc(balanceDocRef);
        
        if (balanceDoc.exists()) {
          const data = balanceDoc.data();
          return this.parseLeaveBalance(data);
        }
        
        // Create default balance if not found
        const defaultBalance = this.getDefaultBalance();
        
        // Save to Firestore
        await setDoc(balanceDocRef, {
          employeeId,
          year: currentYear,
          totalDays: { annual: 30, sick: 15, local: 10, emergency: 15 },
          usedDays: { annual: 0, sick: 0, local: 0, emergency: 0 },
          pendingDays: { annual: 0, sick: 0, local: 0, emergency: 0 },
          lastUpdated: serverTimestamp(),
          createdAt: serverTimestamp()
        });
        
        return defaultBalance;
        
      } catch (error) {
        console.error('Error fetching leave balance:', error);
        return this.getDefaultBalance();
      }
    }
    
    /**
     * Recalculate leave balance from actual leave applications
     * This ensures balance is always accurate
     */
    async recalculateLeaveBalance(employeeId: string): Promise<LeaveBalance> {
      try {
        console.log('🔄 Recalculating leave balance for:', employeeId);
        
        const currentYear = new Date().getFullYear();
        const yearStart = new Date(currentYear, 0, 1);
        const yearEnd = new Date(currentYear, 11, 31);
        
        // Initialize counters
        const usedDays: Record<string, number> = { annual: 0, sick: 0, local: 0, emergency: 0 };
        const pendingDays: Record<string, number> = { annual: 0, sick: 0, local: 0, emergency: 0 };
        const totalDays: Record<string, number> = { annual: 30, sick: 15, local: 10, emergency: 15 };
        
        // Get all leave applications for this employee this year
        const applications = await this.getEmployeeLeaveApplications(employeeId, 100);
        
        applications.forEach(app => {
          // Only count leaves within current year
          if (app.startDate >= yearStart && app.startDate <= yearEnd) {
            const leaveType = app.leaveType;
            const days = app.totalDays || 0;
            
            if (app.hrApprovalStatus === 'approved') {
              // Add to used days
              usedDays[leaveType] = (usedDays[leaveType] || 0) + days;
              
              // Emergency leave also deducts from annual
              if (leaveType === 'emergency') {
                usedDays['annual'] = (usedDays['annual'] || 0) + days;
              }
            } else if (app.hrApprovalStatus === 'pending') {
              // Add to pending days
              pendingDays[leaveType] = (pendingDays[leaveType] || 0) + days;
              
              // Emergency leave also adds to annual pending
              if (leaveType === 'emergency') {
                pendingDays['annual'] = (pendingDays['annual'] || 0) + days;
              }
            }
            // Rejected and cancelled leaves don't count
          }
        });
        
        // Calculate remaining
        const balance: LeaveBalance = {
          annual: {
            total: totalDays.annual,
            used: usedDays.annual,
            pending: pendingDays.annual,
            remaining: Math.max(0, totalDays.annual - usedDays.annual - pendingDays.annual)
          },
          sick: {
            total: totalDays.sick,
            used: usedDays.sick,
            pending: pendingDays.sick,
            remaining: Math.max(0, totalDays.sick - usedDays.sick - pendingDays.sick)
          },
          local: {
            total: totalDays.local,
            used: usedDays.local,
            pending: pendingDays.local,
            remaining: Math.max(0, totalDays.local - usedDays.local - pendingDays.local)
          },
          emergency: {
            total: totalDays.emergency,
            used: usedDays.emergency,
            pending: pendingDays.emergency,
            remaining: Math.max(0, totalDays.emergency - usedDays.emergency - pendingDays.emergency)
          }
        };
        
        // Update balance in Firestore
        const balanceDocRef = doc(db, 'leave_balances', `${employeeId}_${currentYear}`);
        await setDoc(balanceDocRef, {
          employeeId,
          year: currentYear,
          totalDays,
          usedDays,
          pendingDays,
          lastUpdated: serverTimestamp(),
          recalculatedAt: serverTimestamp()
        }, { merge: true });
        
        console.log('✅ Leave balance recalculated:', balance);
        return balance;
        
      } catch (error) {
        console.error('❌ Error recalculating leave balance:', error);
        return this.getDefaultBalance();
      }
    }
    
    private parseLeaveBalance(data: any): LeaveBalance {
      const totalDays = data.totalDays || {};
      const usedDays = data.usedDays || {};
      const pendingDays = data.pendingDays || {};
      
      const calcRemaining = (type: string) => {
        const total = totalDays[type] || this.getDefaultTotal(type);
        const used = usedDays[type] || 0;
        const pending = pendingDays[type] || 0;
        return Math.max(0, total - used - pending);
      };
      
      return {
        annual: {
          total: totalDays.annual || 30,
          used: usedDays.annual || 0,
          pending: pendingDays.annual || 0,
          remaining: calcRemaining('annual')
        },
        sick: {
          total: totalDays.sick || 15,
          used: usedDays.sick || 0,
          pending: pendingDays.sick || 0,
          remaining: calcRemaining('sick')
        },
        local: {
          total: totalDays.local || 10,
          used: usedDays.local || 0,
          pending: pendingDays.local || 0,
          remaining: calcRemaining('local')
        },
        emergency: {
          total: totalDays.emergency || 15,
          used: usedDays.emergency || 0,
          pending: pendingDays.emergency || 0,
          remaining: calcRemaining('emergency')
        }
      };
    }
    
    private getDefaultTotal(type: string): number {
      switch (type) {
        case 'annual': return 30;
        case 'sick': return 15;
        case 'local': return 10;
        case 'emergency': return 15;
        default: return 0;
      }
    }
    
    private getDefaultBalance(): LeaveBalance {
      return {
        annual: { total: 30, used: 0, pending: 0, remaining: 30 },
        sick: { total: 15, used: 0, pending: 0, remaining: 15 },
        local: { total: 10, used: 0, pending: 0, remaining: 10 },
        emergency: { total: 15, used: 0, pending: 0, remaining: 15 }
      };
    }
    
    // ============================================================================
    // SUBMIT LEAVE APPLICATION - WITH DATE OVERLAP CHECK
    // ============================================================================
    
    /**
     * Submit leave application - Direct to HR (skip line manager)
     */
    async submitLeaveApplication(data: LeaveApplicationData): Promise<LeaveApplicationResult> {
      try {
        console.log('📤 Submitting leave application:', data.employeeName, data.leaveType);
        
        // 🆕 CHECK FOR DATE OVERLAP FIRST
        const overlapCheck = await this.checkDateOverlap(
          data.employeeId,
          data.startDate,
          data.endDate
        );
        
        if (overlapCheck.hasOverlap) {
          console.log('❌ Date overlap detected');
          return {
            success: false,
            error: overlapCheck.message || 'Employee already has leave for these dates'
          };
        }
        
        // 1. Upload certificate if provided
        let certificateUrl: string | null = null;
        let certificateFileName: string | null = null;
        
        if (data.certificateFile) {
          const uploadResult = await this.uploadCertificate(data.certificateFile, data.employeeId);
          certificateUrl = uploadResult.url;
          certificateFileName = uploadResult.fileName;
        }
        
        // 2. Generate application ID
        const applicationId = `LA_ENG_${Date.now()}`;
        
        // 3. Create leave application document
        const now = Timestamp.now();
        const applicationDoc = {
          id: applicationId,
          applicationId: applicationId,
          employeeId: data.employeeId,
          employeeName: data.employeeName,
          employeePin: data.employeePin,
          leaveType: data.leaveType,
          startDate: Timestamp.fromDate(data.startDate),
          endDate: Timestamp.fromDate(data.endDate),
          totalDays: data.totalDays,
          reason: data.reason,
          isAlreadyTaken: data.isAlreadyTaken,
          certificateUrl,
          certificateFileName,
          
          // Engineer Portal specific fields
          appliedVia: 'engineer_portal',
          appliedByEngineerId: data.appliedByEngineerId,
          appliedByEngineerName: data.appliedByEngineerName,
          engineerRemarks: data.engineerRemarks || '',
          
          // Status - Direct to HR (skip line manager)
          status: 'pendingHrApproval',
          currentApprovalLevel: 'hrManager',
          lineManagerApprovalStatus: 'auto_approved', // Auto-approved for workers
          lineManagerApprovedAt: now,
          hrApprovalStatus: 'pending',
          
          // Metadata
          applicationDate: now,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          isActive: true,
          year: new Date().getFullYear()
        };
        
        // 4. Use batch write for atomicity
        const batch = writeBatch(db);
        
        // Save to leave_applications collection
        batch.set(doc(db, 'leave_applications', applicationId), applicationDoc);
        console.log('✅ Leave application prepared:', applicationId);
        
        // 5. Create HR approval request
        const hrApprovalData = {
          ...applicationDoc,
          submittedToHrAt: now
        };
        batch.set(doc(db, 'hr_leave_approvals', applicationId), hrApprovalData);
        
        // 6. Update leave balance (add to pending)
        const currentYear = new Date().getFullYear();
        const balanceDocRef = doc(db, 'leave_balances', `${data.employeeId}_${currentYear}`);
        
        // Get current balance first
        const currentBalance = await getDoc(balanceDocRef);
        if (currentBalance.exists()) {
          const balanceData = currentBalance.data();
          const pendingDays = balanceData.pendingDays || { annual: 0, sick: 0, local: 0, emergency: 0 };
          
          // Add to pending
          pendingDays[data.leaveType] = (pendingDays[data.leaveType] || 0) + data.totalDays;
          
          // For emergency leave, also add to annual pending
          if (data.leaveType === 'emergency') {
            pendingDays['annual'] = (pendingDays['annual'] || 0) + data.totalDays;
          }
          
          batch.update(balanceDocRef, {
            pendingDays,
            lastUpdated: serverTimestamp()
          });
        } else {
          // Create new balance
          const pendingDays: Record<string, number> = { annual: 0, sick: 0, local: 0, emergency: 0 };
          pendingDays[data.leaveType] = data.totalDays;
          if (data.leaveType === 'emergency') {
            pendingDays['annual'] = data.totalDays;
          }
          
          batch.set(balanceDocRef, {
            employeeId: data.employeeId,
            year: currentYear,
            totalDays: { annual: 30, sick: 15, local: 10, emergency: 15 },
            usedDays: { annual: 0, sick: 0, local: 0, emergency: 0 },
            pendingDays,
            lastUpdated: serverTimestamp(),
            createdAt: serverTimestamp()
          });
        }
        
        // Commit the batch
        await batch.commit();
        console.log('✅ Batch write completed');
        
        // 7. Send notification to HR (outside batch)
        await this.sendHrNotification(applicationDoc);
        
        return {
          success: true,
          applicationId: applicationId
        };
        
      } catch (error: any) {
        console.error('❌ Error submitting leave application:', error);
        return {
          success: false,
          error: error.message || 'Failed to submit leave application'
        };
      }
    }
    
    // ============================================================================
    // 🆕 NEW: CANCEL LEAVE APPLICATION
    // ============================================================================
    
    /**
     * Cancel a pending leave application
     * Only pending applications can be cancelled
     */
    async cancelLeaveApplication(
      applicationId: string,
      cancelledBy: string,
      cancelledByName: string,
      reason?: string
    ): Promise<{ success: boolean; error?: string }> {
      try {
        console.log('🚫 Cancelling leave application:', applicationId);
        
        const batch = writeBatch(db);
        const now = Timestamp.now();
        
        // Get application data first
        let applicationData: any = null;
        let employeeId = '';
        let leaveType: LeaveType = 'annual';
        let totalDays = 0;
        
        // Check hr_leave_approvals
        const hrApprovalRef = doc(db, 'hr_leave_approvals', applicationId);
        const hrApprovalSnap = await getDoc(hrApprovalRef);
        
        if (hrApprovalSnap.exists()) {
          applicationData = hrApprovalSnap.data();
          employeeId = applicationData.employeeId;
          leaveType = applicationData.leaveType as LeaveType;
          totalDays = applicationData.totalDays || 0;
          
          // Check if can be cancelled (only pending)
          if (applicationData.hrApprovalStatus !== 'pending') {
            return {
              success: false,
              error: `Cannot cancel - application is already ${applicationData.hrApprovalStatus}`
            };
          }
          
          // Update hr_leave_approvals
          batch.update(hrApprovalRef, {
            hrApprovalStatus: 'cancelled',
            status: 'cancelled',
            cancelledAt: now,
            cancelledBy,
            cancelledByName,
            cancellationReason: reason || 'Cancelled by engineer',
            isActive: false,
            updatedAt: now
          });
        }
        
        // Also check leave_applications
        const leaveAppRef = doc(db, 'leave_applications', applicationId);
        const leaveAppSnap = await getDoc(leaveAppRef);
        
        if (leaveAppSnap.exists()) {
          if (!applicationData) {
            applicationData = leaveAppSnap.data();
            employeeId = applicationData.employeeId;
            leaveType = applicationData.leaveType as LeaveType;
            totalDays = applicationData.totalDays || 0;
            
            // Check if can be cancelled
            const status = applicationData.hrApprovalStatus || applicationData.status;
            if (status !== 'pending' && status !== 'pendingHrApproval') {
              return {
                success: false,
                error: `Cannot cancel - application is already ${status}`
              };
            }
          }
          
          batch.update(leaveAppRef, {
            hrApprovalStatus: 'cancelled',
            status: 'cancelled',
            cancelledAt: now,
            cancelledBy,
            cancelledByName,
            cancellationReason: reason || 'Cancelled by engineer',
            isActive: false,
            updatedAt: now
          });
        }
        
        if (!applicationData) {
          return {
            success: false,
            error: 'Leave application not found'
          };
        }
        
        // Restore leave balance (remove from pending)
        const currentYear = new Date().getFullYear();
        const balanceDocRef = doc(db, 'leave_balances', `${employeeId}_${currentYear}`);
        const balanceSnap = await getDoc(balanceDocRef);
        
        if (balanceSnap.exists()) {
          const balanceData = balanceSnap.data();
          const pendingDays = balanceData.pendingDays || { annual: 0, sick: 0, local: 0, emergency: 0 };
          
          // Remove from pending
          pendingDays[leaveType] = Math.max(0, (pendingDays[leaveType] || 0) - totalDays);
          
          // For emergency leave, also remove from annual pending
          if (leaveType === 'emergency') {
            pendingDays['annual'] = Math.max(0, (pendingDays['annual'] || 0) - totalDays);
          }
          
          batch.update(balanceDocRef, {
            pendingDays,
            lastUpdated: now
          });
        }
        
        // Commit the batch
        await batch.commit();
        
        // Send cancellation notification
        await this.sendCancellationNotification(applicationData, cancelledByName, reason);
        
        console.log('✅ Leave application cancelled successfully');
        return { success: true };
        
      } catch (error: any) {
        console.error('❌ Error cancelling leave application:', error);
        return {
          success: false,
          error: error.message || 'Failed to cancel leave application'
        };
      }
    }
    
    /**
     * Send cancellation notification
     */
    private async sendCancellationNotification(
      applicationData: any,
      cancelledByName: string,
      reason?: string
    ): Promise<void> {
      try {
        const notification = {
          type: 'leave_cancelled',
          title: '🚫 Leave Application Cancelled',
          body: `${applicationData.employeeName}'s ${applicationData.leaveType} leave (${applicationData.totalDays} days) has been cancelled`,
          message: `${cancelledByName} cancelled the leave application.${reason ? ` Reason: ${reason}` : ''}`,
          
          applicationId: applicationData.id || applicationData.applicationId,
          employeeId: applicationData.employeeId,
          employeeName: applicationData.employeeName,
          leaveType: applicationData.leaveType,
          totalDays: applicationData.totalDays,
          
          cancelledBy: cancelledByName,
          cancellationReason: reason,
          
          targetRole: 'hr_manager',
          isRead: false,
          priority: 'normal',
          category: 'leave_management',
          
          timestamp: serverTimestamp(),
          createdAt: serverTimestamp()
        };
        
        await addDoc(collection(db, 'hr_notifications'), notification);
        await addDoc(collection(db, 'hr_live_notifications'), notification);
        
        console.log('✅ Cancellation notification sent');
        
      } catch (error) {
        console.error('❌ Error sending cancellation notification:', error);
      }
    }
    
    /**
     * Upload certificate file
     */
    private async uploadCertificate(file: File, employeeId: string): Promise<{ url: string; fileName: string }> {
      try {
        const timestamp = Date.now();
        const cleanFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
        const storagePath = `leave_certificates/${employeeId}/${timestamp}_${cleanFileName}`;
        
        const storageRef = ref(storage, storagePath);
        await uploadBytes(storageRef, file);
        const downloadUrl = await getDownloadURL(storageRef);
        
        console.log('✅ Certificate uploaded:', cleanFileName);
        
        return {
          url: downloadUrl,
          fileName: cleanFileName
        };
        
      } catch (error) {
        console.error('❌ Error uploading certificate:', error);
        throw new Error('Failed to upload certificate');
      }
    }
    
    /**
     * Send notification to HR about new leave application
     */
    private async sendHrNotification(applicationDoc: any): Promise<void> {
      try {
        const notification = {
          type: 'engineer_leave_application',
          title: '📋 New Leave Application (Engineer Portal)',
          body: `${applicationDoc.employeeName} - ${applicationDoc.leaveType.charAt(0).toUpperCase() + applicationDoc.leaveType.slice(1)} Leave (${applicationDoc.totalDays} days)`,
          message: `Engineer ${applicationDoc.appliedByEngineerName} applied ${applicationDoc.leaveType} leave for ${applicationDoc.employeeName}. Direct HR approval required.`,
          
          applicationId: applicationDoc.id,
          employeeId: applicationDoc.employeeId,
          employeeName: applicationDoc.employeeName,
          leaveType: applicationDoc.leaveType,
          totalDays: applicationDoc.totalDays,
          startDate: applicationDoc.startDate,
          endDate: applicationDoc.endDate,
          
          appliedByEngineerId: applicationDoc.appliedByEngineerId,
          appliedByEngineerName: applicationDoc.appliedByEngineerName,
          appliedVia: 'engineer_portal',
          
          targetRole: 'hr_manager',
          isRead: false,
          priority: applicationDoc.leaveType === 'emergency' ? 'urgent' : 'high',
          requiresAction: true,
          actionType: 'hr_approval',
          category: 'leave_management',
          
          timestamp: serverTimestamp(),
          createdAt: serverTimestamp()
        };
        
        // Add to hr_notifications
        await addDoc(collection(db, 'hr_notifications'), notification);
        
        // Add to hr_live_notifications for real-time
        await addDoc(collection(db, 'hr_live_notifications'), {
          ...notification,
          isUrgent: applicationDoc.leaveType === 'emergency'
        });
        
        console.log('✅ HR notification sent');
        
      } catch (error) {
        console.error('❌ Error sending HR notification:', error);
        // Don't throw
      }
    }
    
    // ============================================================================
    // VALIDATION HELPERS
    // ============================================================================
    
    /**
     * Check if certificate is required
     */
    isCertificateRequired(leaveType: LeaveType, isAlreadyTaken: boolean): boolean {
      // Emergency leave NEVER requires certificate
      if (leaveType === 'emergency') {
        return false;
      }
      
      // Sick leave: required if already taken
      if (leaveType === 'sick' && isAlreadyTaken) {
        return true;
      }
      
      // Other leave types: required if already taken
      if (isAlreadyTaken && (leaveType === 'annual' || leaveType === 'local')) {
        return true;
      }
      
      return false;
    }
    
    /**
     * Calculate total days between dates
     */
    calculateTotalDays(startDate: Date, endDate: Date): number {
      if (startDate > endDate) return 0;
      const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      return diffDays;
    }
    
    /**
     * Validate leave application
     */
    validateLeaveApplication(
      data: Partial<LeaveApplicationData>,
      balance: LeaveBalance
    ): { valid: boolean; error?: string } {
      
      if (!data.employeeId || !data.employeeName) {
        return { valid: false, error: 'Employee not selected' };
      }
      
      if (!data.leaveType) {
        return { valid: false, error: 'Please select leave type' };
      }
      
      if (!data.startDate || !data.endDate) {
        return { valid: false, error: 'Please select start and end dates' };
      }
      
      if (data.startDate > data.endDate) {
        return { valid: false, error: 'Start date cannot be after end date' };
      }
      
      const totalDays = data.totalDays || this.calculateTotalDays(data.startDate, data.endDate);
      
      // Check balance
      if (data.leaveType === 'emergency') {
        if (balance.emergency.remaining < totalDays && balance.annual.remaining < totalDays) {
          return { 
            valid: false, 
            error: `Insufficient balance. Emergency: ${balance.emergency.remaining} days, Annual: ${balance.annual.remaining} days` 
          };
        }
      } else {
        const typeBalance = balance[data.leaveType];
        if (typeBalance.remaining < totalDays) {
          return { 
            valid: false, 
            error: `Insufficient ${data.leaveType} leave balance. Available: ${typeBalance.remaining} days` 
          };
        }
      }
      
      // Check certificate for already taken leave
      if (this.isCertificateRequired(data.leaveType, data.isAlreadyTaken || false)) {
        if (!data.certificateFile) {
          return { valid: false, error: 'Certificate is required for this leave type' };
        }
      }
      
      return { valid: true };
    }
  }
  
  export const engineerLeaveService = new EngineerLeaveService();